import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { presupuestosService, type FiltrosPresupuestos } from '@/services/presupuestosService'
import { useClienteDatosNombre } from '@/hooks/useClientes'
import { resolverBaseNombre, nombreArchivoPresupuesto } from '@/components/presupuestos/presupuesto-utils'
import { useNotificacionesModulo } from '@/core/hooks/useNotificacionesModulo'
import { PLANTILLAS_NOTIFICACION } from '@/core/types/notificaciones'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import type { Database, Presupuesto } from '@/types/database'

type PresupuestoInsert = Database['public']['Tables']['presupuestos']['Insert']
type PresupuestoUpdate = Database['public']['Tables']['presupuestos']['Update']

// Query keys
export const presupuestosKeys = {
  all: ['presupuestos'] as const,
  lists: () => [...presupuestosKeys.all, 'list'] as const,
  list: (filtros?: FiltrosPresupuestos) => [...presupuestosKeys.lists(), filtros] as const,
  details: () => [...presupuestosKeys.all, 'detail'] as const,
  detail: (id: string) => [...presupuestosKeys.details(), id] as const,
  estadisticas: (orgId?: string) => [...presupuestosKeys.all, 'estadisticas', orgId] as const,
}

// Hook para listar presupuestos
export function usePresupuestos(filtros?: FiltrosPresupuestos) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: presupuestosKeys.list(filtros),
    queryFn: () => presupuestosService.listar(supabase!, filtros),
    enabled: !!supabase && !supabaseLoading,
    staleTime: 30_000,
  })
}

// Hook para obtener un presupuesto por ID
export function usePresupuesto(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: presupuestosKeys.detail(id),
    queryFn: () => presupuestosService.obtenerPorId(supabase!, id),
    enabled: !!supabase && !supabaseLoading && !!id,
    staleTime: 30_000,
  })
}

// Hook para el nombre de archivo del presupuesto (ORR-120):
// `Presupuesto-{Apellido}.pdf` (físicas) / `Presupuesto-{RazónSocial}.pdf` (jurídicas).
// Usa el apellido/razón social real del cliente (consulta liviana); si el
// presupuesto no tiene cliente_id, deriva del cliente_nombre denormalizado.
export function useNombreArchivoPresupuesto(presupuesto: Presupuesto | null | undefined): string {
  const { data: datosCliente } = useClienteDatosNombre(presupuesto?.cliente_id)

  return useMemo(() => {
    if (!presupuesto) return 'Presupuesto.pdf'
    const base = resolverBaseNombre({
      tipo: datosCliente?.tipo,
      apellido: datosCliente?.apellido,
      razonSocial: datosCliente?.razon_social,
      nombreCompleto: datosCliente?.nombre_completo,
      clienteNombre: presupuesto.cliente_nombre,
      cuit: presupuesto.cliente_cuit,
    })
    return nombreArchivoPresupuesto(base, presupuesto.numero_presupuesto)
  }, [presupuesto, datosCliente])
}

// Hook para estadisticas
export function useEstadisticasPresupuestos() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: presupuestosKeys.estadisticas(organizacionId ?? undefined),
    queryFn: () => presupuestosService.obtenerEstadisticas(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    staleTime: 60_000,
  })
}

// Hook para crear presupuesto
export function useCrearPresupuesto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (presupuesto: PresupuestoInsert) => {
      assertPermiso(puede, 'crearPresupuesto')
      return presupuestosService.crear(supabase!, presupuesto)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.estadisticas() })
      toast.success('Presupuesto creado exitosamente')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al crear presupuesto')
    },
  })
}

// Hook para actualizar presupuesto
export function useActualizarPresupuesto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PresupuestoUpdate }) => {
      assertPermiso(puede, 'editarPresupuesto')
      return presupuestosService.actualizar(supabase!, id, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.estadisticas() })
      toast.success('Presupuesto actualizado exitosamente')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al actualizar presupuesto')
    },
  })
}

// Hook para eliminar presupuesto
export function useEliminarPresupuesto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'eliminarPresupuesto')
      return presupuestosService.eliminar(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.estadisticas() })
      toast.success('Presupuesto eliminado exitosamente')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al eliminar presupuesto')
    },
  })
}

// Hook para cambiar estado
export function useCambiarEstadoPresupuesto() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: Presupuesto['estado'] }) => {
      // ORR-200 B8: acciones de negocio con flag fino; otras transiciones siguen con editarPresupuesto
      if (estado === 'enviado') assertPermiso(puede, 'enviarPresupuesto')
      else if (estado === 'aceptado') assertPermiso(puede, 'aceptarPresupuesto')
      else if (estado === 'rechazado') assertPermiso(puede, 'rechazarPresupuesto')
      else assertPermiso(puede, 'editarPresupuesto')
      return presupuestosService.cambiarEstado(supabase!, id, estado)
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.estadisticas() })
      toast.success('Estado actualizado')

      // Notif Fase 4: cambio de estado del presupuesto
      // Reemplaza el bug pre-existente de auto-notificación (notificarSistema
      // al propio user.email). Ahora notifica a los responsables del cliente
      // + creador del presupuesto, excluyendo al actor.
      if (!supabase || !data?.cliente_id) return
      const clienteNombre = data.cliente_nombre || 'Cliente sin nombre'
      const numero = data.numero ?? 0
      const total = data.total ?? 0
      const actor = user?.name || user?.email || 'Alguien'

      // Destinatarios: responsables del cliente. Agregar el creador si no es el actor.
      const destinatarios = await responsablesCliente(data.cliente_id, 'verPresupuestos')
      if (data.created_by && data.created_by !== user?.email && !destinatarios.includes(data.created_by)) {
        destinatarios.push(data.created_by)
      }
      if (destinatarios.length === 0) return

      let plantilla: ReturnType<typeof PLANTILLAS_NOTIFICACION.presupuestoEnviado> | null = null
      if (variables.estado === 'enviado') {
        plantilla = PLANTILLAS_NOTIFICACION.presupuestoEnviado(clienteNombre, numero, total, actor)
      } else if (variables.estado === 'aceptado') {
        plantilla = PLANTILLAS_NOTIFICACION.presupuestoAceptado(clienteNombre, numero, total)
      } else if (variables.estado === 'rechazado') {
        plantilla = PLANTILLAS_NOTIFICACION.presupuestoRechazado(clienteNombre, numero)
      }

      if (plantilla) {
        try {
          await notificar({
            ...plantilla,
            destinatarios,
            enlace: `/presupuestos/${data.id}`,
          })
        } catch (err) {
          console.error('[notificaciones] error notificando cambio de presupuesto:', err)
        }
      }
    },
    onError: (error) => {
      handleMutationError(error, 'Error al cambiar estado')
    },
  })
}

// Hook para duplicar presupuesto
export function useDuplicarPresupuesto() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'crearPresupuesto')
      return presupuestosService.duplicar(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presupuestosKeys.estadisticas() })
      toast.success('Presupuesto duplicado exitosamente')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al duplicar presupuesto')
    },
  })
}
